import React, { Component, createRef } from 'react';
import { IconFileDownload, TextButton } from 'cdk-radial';
import {
  Endpoints,
  ApiEndpoints,
  EndpointsList,
  EndpointListItem,
  EndpointListInfo,
  SpecSectionItem,
  SpecSection,
  ApiHeaderDetails,
  AsyncApiSpecCodeBody
} from './styledComponents';
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import './api-explorer.css';
import * as YAML from 'yaml';
import config from '../../config/config';
import { SCROLL_DELAY_MS } from '../../utils/constants';
import { CopyToClipboard } from '../common/CopyToClipboard';

const ApiSpecYamlText = ({ spec, selectedSection }) => {

  if (spec && typeof spec === 'object') {

    const orderedSpec = {
      asyncapi: spec.asyncapi
    };
    Object.assign(orderedSpec, spec);
    const fullDoc = new YAML.Document();
    fullDoc.contents = orderedSpec;
    const fullYaml = fullDoc.toString();
    const allLines = fullYaml.split(/\r\n|\r|\n/);

    const sectionLineRanges = {};
    const sectionKeys = Object.keys(orderedSpec).filter(key => key !== null);

    sectionKeys.forEach((section, index) => {
      if (!section) return;
      const sectionStartIndex = allLines.findIndex((line) => {
        return line && line.match(new RegExp(`^${section.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:`));
      });

      if (sectionStartIndex !== -1) {
        let sectionEndIndex = allLines.length;

        for (let i = index + 1; i < sectionKeys.length; i++) {
          const nextSection = sectionKeys[i];
          if (!nextSection) continue;
          const nextIndex = allLines.findIndex((line, idx) => {
            return idx > sectionStartIndex && line && nextSection && line.match(new RegExp(`^${nextSection.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:`));
          });
          if (nextIndex !== -1) {
            sectionEndIndex = nextIndex;
            break;
          }
        }

        sectionLineRanges[section] = {
          start: sectionStartIndex + 1,
          end: sectionEndIndex
        };
      }
    });

    return (
      <div className="api-dir-pkg_yaml-spec-section">
        <AsyncApiSpecCodeBody>
          <SyntaxHighlighter
            language="yaml"
            customStyle={{
              marginTop: '0px',
              marginBottom: '0px',
              paddingTop: '0px',
              paddingBottom: '0px'
            }}
            showLineNumbers={true}
            startingLineNumber={1}
            wrapLongLines={true}
            lineNumberStyle={{
              minWidth: '50px',
              paddingRight: '20px',
              display: 'inline-block',
              textAlign: 'right',
              color: 'rgba(255, 255, 255, 0.5)',
              userSelect: 'none'
            }}
            codeTagProps={{
              style: {
                fontFamily: 'Source Code Pro !important',
                color: 'rgb(215, 71, 168)',
                lineHeight: 'initial'
              }
            }}
            lineProps={(lineNumber) => {
              // Check if this line belongs to the selected section
              let isInSelectedSection = false;

              if (selectedSection && sectionLineRanges[selectedSection]) {
                const range = sectionLineRanges[selectedSection];
                isInSelectedSection = lineNumber >= range.start && lineNumber <= range.end;
              }

              return {
                'data-line-number': lineNumber,
                'data-section-start': selectedSection && sectionLineRanges[selectedSection] && lineNumber === sectionLineRanges[selectedSection].start ? 'true' : 'false',
                style: {
                  backgroundColor: isInSelectedSection ? '#41494e' : 'transparent',
                  display: 'block'
                }
              };
            }}
          >
            {fullYaml}
          </SyntaxHighlighter>
        </AsyncApiSpecCodeBody>
      </div>
    );
  }
};

class AsyncApiExplorer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      content: null,
      specSections: [],
      selectedSection: null,
      brokerUrl: null
    };
    this.specContainerRef = createRef();
  }

  setSection = section => {
    if (!section) return;

    this.setState({ selectedSection: section }, () => {
      setTimeout(() => {
        if (this.specContainerRef.current && this.state.content) {
          const container = this.specContainerRef.current;

          const sectionStartLine = container.querySelector('span[data-section-start="true"]');

          if (sectionStartLine && sectionStartLine.scrollIntoView) {
            sectionStartLine.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          }
        }
      }, SCROLL_DELAY_MS);
    });
  };

  componentDidMount = async () => {
    const apiInstance = this.props?.api?.environments?.[0]?.instances?.[0];
    if (!apiInstance || !apiInstance.spec) return;

    this.setState({
      content: apiInstance.spec,
      brokerUrl: config?.api?.brokerUrl,
      specSections: Object.keys(apiInstance.spec).filter(key => key !== null)
    });
  };

  render() {
    const {
      state: { content, brokerUrl, specSections, selectedSection }
    } = this;

    return (
      <div className="api-dir-pkg_api-tab-container api-dir-pkg_generic-style-for-browsers">
        <ApiHeaderDetails className="header-section">
          <div className="header-content">
            <div className="api-dir-pkg_api-details-title">
              {this.props.bundleHeader ? this.props.api?.name : "Async API Specifications"}
            </div>
            <TextButton
              className="api-dir-pkg_download_spec_btn"
              data-cy="download_spec_btn"
              onClick={() => {
                if (this.props.downloadSpec && content) {
                  this.props.downloadSpec(content);
                }
              }}
              icon={<IconFileDownload/>}
              text="API Spec"
            />
          </div>
        </ApiHeaderDetails>
        <ApiEndpoints>
          <EndpointsList>
            <Endpoints className="api-dir-pkg_spec-sections">
              {specSections && specSections.map(item => {
                //skip 'id' and 'async-api' properties as a section
                if (!item || item === 'id' || item === 'asyncapi') return null;
                return (
                  <EndpointListItem key={item} id={item}>
                    <EndpointListInfo
                      onClick={() => item && this.setSection(item)}
                      method={'asyncApiSpecSection'}
                      isSelected={item && item === selectedSection}
                    >
                      <SpecSectionItem method={item}>{item}</SpecSectionItem>
                    </EndpointListInfo>
                  </EndpointListItem>
                );
              })}
            </Endpoints>
          </EndpointsList>
          {content && (
            <SpecSection>
              <div>
                {brokerUrl && (
                  <div>
                    <div className="api-dir-pkg_broker-url-text"> Broker URL </div>
                    <div
                      className="api-dir-pkg_broker-url"
                    >
                      {brokerUrl}
                      <CopyToClipboard textToCopy={brokerUrl} fill={'#fff'} size={20} />
                    </div>
                  </div>
                )}
                <div ref={this.specContainerRef} style={{ maxHeight: '800px', overflow: 'auto' }}>
                  {this.state.content && <ApiSpecYamlText
                    spec={this.state.content}
                    selectedSection={this.state.selectedSection}
                  />}
                </div>
              </div>
            </SpecSection>
          )}
        </ApiEndpoints>
      </div>
    );
  }
}

export default AsyncApiExplorer;
